// ─── Core: loaded on every page ────────────────────────────────────────────
// smooth-scroll and animation self-defer via requestIdleCallback internally.
import './components/smooth-scroll';
import './components/animation';
import './components/input-mode-detection';
import './components/owe-analytics';
import { observeAndImport } from './helpers';

// ─── Deferred core components ──────────────────────────────────────────────
// header-nav, skip-link and modal are essential but their JS enhancements
// can safely run after first paint. Dynamic imports move them out of the
// synchronous main chunk, reducing TBT.

import(/* webpackChunkName: "header-nav" */ './components/header-nav');
import(/* webpackChunkName: "skip-link" */ './components/skip-link');
import(/* webpackChunkName: "cookie-banner" */ './components/cookie-banner');

if (document.querySelector('.home-hero-wrapper')) {
  import(/* webpackChunkName: "home-hero" */ './components/home-hero');
}

// modal: only the MODAL_EVENTS constants are needed synchronously by other
// async chunks (video-player, enquiry-form-modal). The full modal code is
// loaded dynamically — webpack deduplicates the constants module.
if (document.querySelector('[data-modal-trigger]')) {
  import(/* webpackChunkName: "modal" */ './components/modal');
}

// ─── Page-specific vanilla JS components (lazy-loaded) ───────────────────────
// Each import() fires only when its root element is present in the DOM.
// The script runs after </body> so document.querySelector is safe inline.

if (document.querySelector('[data-accordion-block-trigger]')) {
  import(/* webpackChunkName: "accordion-block" */ './components/accordion-block');
}

if (document.querySelector('[data-sidebar-accordion-trigger]')) {
  import(/* webpackChunkName: "article-sidebar-accordion" */ './components/article-sidebar-accordion');
}

if (document.querySelector('[data-breadcrumb]')) {
  import(/* webpackChunkName: "breadcrumb" */ './components/breadcrumb');
}

if (document.querySelector('.expert-profile__call-btn')) {
  import(/* webpackChunkName: "call-button" */ './components/call-button');
}

observeAndImport(
  '[data-capabilities-ticker]',
  () => import(/* webpackChunkName: "capabilities-ticker" */ './components/capabilities-ticker')
);

if (document.querySelector('.seventy-thirty-content')) {
  import(/* webpackChunkName: "content-area-reorder" */ './components/content-area-reorder');
}

observeAndImport(
  '[data-content-summary-grid-tabs]',
  () => import(/* webpackChunkName: "content-summary-grid-block" */ './components/content-summary-grid-block')
);

if (document.querySelector('[data-multi-form-tabs]')) {
  import(/* webpackChunkName: "multi-form-tabs" */ './components/multi-form-tabs');
}

if (document.querySelector('.EPiServerForms')) {
  import(/* webpackChunkName: "epi-form" */ './components/epi-form');
}

if (document.querySelector('[data-form-double-row]')) {
  import(/* webpackChunkName: "form-double-field-row" */ './components/form-double-field-row');
}

if (document.querySelector('[data-toast-dismiss]')) {
  import(/* webpackChunkName: "form-toast-message" */ './components/form-toast-message');
}

observeAndImport(
  '[data-insights-carousel]',
  () => import(/* webpackChunkName: "insights-carousel" */ './components/insights-carousel')
);

if (document.querySelector('[data-insights-landing-search]')) {
  import(/* webpackChunkName: "insights-landing-search" */ './components/insights-landing-search');
}

if (document.querySelector('[data-anchor-nav]')) {
  import(/* webpackChunkName: "hero-quicklinks" */ './components/hero-quicklinks');
}

if (document.querySelector('[data-password-protection]')) {
  import(/* webpackChunkName: "password-protection" */ './components/password-protection');
}

if (document.querySelector('[data-pft-row]')) {
  import(/* webpackChunkName: "product-features-table" */ './components/product-features-table');
}

observeAndImport(
  '[data-product-list-block]',
  () => import(/* webpackChunkName: "product-list-block" */ './components/product-list-block')
);

if (document.querySelector('[data-region-transition-modal]')) {
  import(/* webpackChunkName: "region-transition-modal" */ './components/region-transition-modal');
}

if (document.querySelector('.search-overlay')) {
  import(/* webpackChunkName: "search-overlay" */ './components/search-overlay');
}

observeAndImport(
  '[data-stories-carousel]',
  () => import(/* webpackChunkName: "stories-carousel" */ './components/stories-carousel')
);

if (document.querySelector('[data-video-player]')) {
  import(/* webpackChunkName: "video-player" */ './components/video-player');
}

if (document.querySelector('[data-custom-select]') || document.querySelector('.Form__Element.FormSelection')) {
  import(/* webpackChunkName: "custom-select" */ './components/custom-select');
  import(/* webpackChunkName: "epi-form-select" */ './components/epi-form-select');
}

if (document.querySelector('[data-enquiry-form]')) {
  import(/* webpackChunkName: "enquiry-form-modal" */ './components/enquiry-form-modal');
}

// ─── React components (lazy-loaded) ──────────────────────────────────────────

if (document.querySelector('#search-root')) {
  import(/* webpackChunkName: "react-search" */ './react/Search');
}

observeAndImport(
  '[data-office-list-root]',
  () => import(/* webpackChunkName: "react-office-list" */ './react/OfficeListBlock')
);

observeAndImport(
  '#location-office-list',
  () => import(/* webpackChunkName: "react-location-office-list" */ './react/LocationPageOfficeList')
);

observeAndImport(
  '#region-picker-root',
  () => import(/* webpackChunkName: "react-region-picker" */ './react/RegionPicker')
);

observeAndImport(
  '[data-stats-block-root]',
  () => import(/* webpackChunkName: "react-stats-block" */ './react/StatsBlock')
);

observeAndImport(
  '[data-large-experts-list-root]',
  () => import(/* webpackChunkName: "react-large-experts-list" */ './react/LargeExpertsListBlock')
);

observeAndImport(
  '[data-product-finder-cta-root]',
  () => import(/* webpackChunkName: "react-product-finder-cta" */ './react/ProductFinderCtaBlock')
);
